/**
 * MediaRecorder Video Exporter
 *
 * Exports timeline to WebM using native MediaRecorder API
 * Much simpler and more reliable than custom WebCodecs implementation
 */

import { Clip, Track, SubtitleStyle } from './types';
import { FrameRenderer } from './frame-renderer';
import { getClipExportSourceUrl } from './utils/clip-helpers';

// Aspect ratio configurations
export const ASPECT_RATIOS = {
  '16:9': { width: 1920, height: 1080 },
  '1:1': { width: 1080, height: 1080 },
  '9:16': { width: 1080, height: 1920 },
} as const;

export type AspectRatio = keyof typeof ASPECT_RATIOS;

export interface ExportConfig {
  aspectRatio: AspectRatio;
  fps: number;
  videoBitrate: number;
  audioBitrate: number;
  audioSampleRate: number;
  format: string;
}

export interface ExportProgress {
  phase: 'preparing' | 'encoding' | 'muxing' | 'complete' | 'error';
  progress: number; // 0-100
  message: string;
  currentFrame?: number;
  totalFrames?: number;
}

export type ProgressCallback = (progress: ExportProgress) => void;

export class MediaRecorderExporter {
  private config: ExportConfig;
  private progressCallback: ProgressCallback;
  private aborted = false;
  private frameRenderer?: FrameRenderer;

  constructor(config: ExportConfig, progressCallback: ProgressCallback) {
    this.config = config;
    this.progressCallback = progressCallback;
  }

  /**
   * Main export function
   */
  async export(
    clips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<Blob> {
    try {
      this.aborted = false;

      // Phase 1: Preparation
      this.updateProgress({
        phase: 'preparing',
        progress: 0,
        message: 'Préparation de l\'export...'
      });

      // Filter visible and audible clips
      const visibleClips = this.filterVisibleClips(clips, tracks);
      const audioClips = this.filterAudioClips(clips, tracks);

      // Calculate timeline duration
      const duration = this.calculateDuration(clips);
      const totalFrames = Math.ceil(duration * this.config.fps);

      this.updateProgress({
        phase: 'preparing',
        progress: 10,
        message: `${visibleClips.length} clips vidéo, ${audioClips.length} clips audio`,
        totalFrames
      });

      // Phase 2: Setup canvas and streams
      const resolution = ASPECT_RATIOS[this.config.aspectRatio];
      const canvas = window.document.createElement('canvas');
      canvas.width = resolution.width;
      canvas.height = resolution.height;
      const ctx = canvas.getContext('2d', {
        alpha: true,
      });

      if (!ctx) {
        throw new Error('Impossible de créer le contexte canvas');
      }

      this.updateProgress({
        phase: 'preparing',
        progress: 20,
        message: 'Initialisation du renderer...'
      });

      // Initialize frame renderer and preload media
      this.frameRenderer = new FrameRenderer(resolution.width, resolution.height);
      await this.frameRenderer.preloadMedia(clips); // Preload all clips, not just visible ones

      this.updateProgress({
        phase: 'preparing',
        progress: 30,
        message: 'Préparation de l\'encodeur...'
      });

      // Phase 3: Render frames to canvas and capture with MediaRecorder
      const blob = await this.recordVideo(
        canvas,
        ctx,
        duration,
        totalFrames,
        clips, // Pass all clips
        tracks, // Pass all tracks
        audioClips,
        activeSubtitleLanguage,
        subtitleStyle
      );

      this.updateProgress({
        phase: 'complete',
        progress: 100,
        message: 'Export terminé !'
      });

      return blob;

    } catch (error) {
      this.updateProgress({
        phase: 'error',
        progress: 0,
        message: `Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`
      });
      throw error;
    } finally {
      this.cleanup();
    }
  }

  /**
   * Record video using MediaRecorder
   */
  private async recordVideo(
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    duration: number,
    totalFrames: number,
    allClips: Clip[],
    allTracks: Track[],
    audioClips: Clip[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<Blob> {
    // Create audio context for mixing
    // FIX: Use `window.AudioContext` to prevent type errors when DOM libs are missing.
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: this.config.audioSampleRate });
    const audioDestination = audioContext.createMediaStreamDestination();

    // Load and mix audio
    await this.setupAudioMix(audioContext, audioDestination, audioClips, duration);

    // Get canvas stream
    const canvasStream = canvas.captureStream(this.config.fps);

    // Combine video and audio streams
    // FIX: Use `(window as any).MediaStream` to prevent type errors when DOM libs are missing.
    const combinedStream = new (window as any).MediaStream([
      ...canvasStream.getVideoTracks(),
      ...audioDestination.stream.getAudioTracks()
    ]);

    // Setup MediaRecorder
    const mimeType = this.getBestMimeType(this.config.format);
    // FIX: Use `(window as any).MediaRecorder` to prevent type errors when DOM libs are missing.
    const mediaRecorder = new (window as any).MediaRecorder(combinedStream, {
      mimeType,
      videoBitsPerSecond: this.config.videoBitrate,
      audioBitsPerSecond: this.config.audioBitrate,
    });

    const recordedChunks: Blob[] = [];

    return new Promise<Blob>((resolve, reject) => {
      mediaRecorder.ondataavailable = (event: any) => {
        if (event.data.size > 0) {
          recordedChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunks, { type: mimeType });
        audioContext.close();
        resolve(blob);
      };

      mediaRecorder.onerror = (event: any) => {
        audioContext.close();
        reject(new Error(`MediaRecorder error: ${event}`));
      };

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      audioContext.resume();

      // Render frames
      this.renderFramesInRealtime(
        ctx,
        totalFrames,
        allClips,
        allTracks,
        activeSubtitleLanguage,
        subtitleStyle
      ).then(() => {
        // Stop recording after all frames are rendered
        setTimeout(() => {
          mediaRecorder.stop();
        }, 100);
      }).catch(reject);
    });
  }

  /**
   * Setup audio mix
   */
  private async setupAudioMix(
    audioContext: AudioContext,
    destination: MediaStreamAudioDestinationNode,
    audioClips: Clip[],
    duration: number
  ): Promise<void> {
    if (audioClips.length === 0) {
      // Create silent track
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      gain.gain.value = 0;
      oscillator.connect(gain);
      gain.connect(destination);
      oscillator.start();
      return;
    }

    // Load and schedule audio clips
    for (const clip of audioClips) {
      try {
        const audioUrl = getClipExportSourceUrl(clip);
        if (!audioUrl) continue;

        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        // Create source and schedule playback
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;

        // Apply volume
        const gainNode = audioContext.createGain();
        gainNode.gain.value = clip.volume ?? 1.0;

        // Calculate clip timing
        const startTime = clip.start;
        const trimStart = clip.trimStart || 0;
        const clipDuration = Math.min(clip.duration, audioBuffer.duration - trimStart);

        source.connect(gainNode);
        gainNode.connect(destination);

        // Schedule playback
        source.start(startTime, trimStart, clipDuration);
      } catch (error) {
        console.warn(`Failed to load audio for clip ${clip.name}:`, error);
      }
    }
  }

  /**
   * Render frames in realtime
   */
  private async renderFramesInRealtime(
    ctx: CanvasRenderingContext2D,
    totalFrames: number,
    allClips: Clip[],
    allTracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<void> {
    const frameTime = 1000 / this.config.fps; // ms per frame
    const startTime = Date.now();

    for (let frame = 0; frame < totalFrames; frame++) {
      if (this.aborted) {
        throw new Error('Export annulé');
      }

      const currentTime = frame / this.config.fps;

      // Render frame (SANS burn de sous-titres)
      if (this.frameRenderer) {
        await this.frameRenderer.renderFrame(
          ctx,
          currentTime,
          allClips,
          allTracks,
          '', // Pas de langue de sous-titres => pas de burn
          subtitleStyle
        );
      }

      // Update progress
      if (frame % 10 === 0) {
        const progress = 30 + Math.floor((frame / totalFrames) * 65);
        this.updateProgress({
          phase: 'encoding',
          progress,
          message: `Encodage frame ${frame}/${totalFrames}`,
          currentFrame: frame,
          totalFrames
        });
      }

      // Wait for next frame time (realtime pacing)
      const targetTime = startTime + (frame + 1) * frameTime;
      const now = Date.now();
      if (targetTime > now) {
        await new Promise(resolve => setTimeout(resolve, targetTime - now));
      }
    }

    this.updateProgress({
      phase: 'muxing',
      progress: 95,
      message: 'Finalisation du fichier...'
    });
  }

  /**
   * Get best supported MIME type, preferring the requested format.
   */
  private getBestMimeType(preferredFormat?: string): string {
    const mp4Type = 'video/mp4;codecs=avc1.42E01E,mp4a.40.2';
    const webmTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ];

    if (preferredFormat === 'mp4' && (window as any).MediaRecorder.isTypeSupported(mp4Type)) {
      return mp4Type;
    }

    if (preferredFormat === 'mp4') {
      console.warn('MP4 export not supported by this browser. Falling back to WebM.');
    }

    for (const type of webmTypes) {
      if ((window as any).MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return 'video/webm'; // Fallback
  }

  /**
   * Abort the export
   */
  abort() {
    this.aborted = true;
    this.cleanup();
  }

  /**
   * Filter visible clips
   */
  private filterVisibleClips(clips: Clip[], tracks: Track[]): Clip[] {
    return clips.filter(clip => {
      const track = tracks.find(t => t.id === clip.trackId);
      return track?.isVisible &&
        (clip.type === 'video' || clip.type === 'image' || clip.type === 'text' || clip.type === 'zoom' || clip.type === 'synthe' || clip.type === 'panel');
    });
  }

  /**
   * Filter audio clips
   */
  private filterAudioClips(clips: Clip[], tracks: Track[]): Clip[] {
    return clips.filter(clip => {
      const track = tracks.find(t => t.id === clip.trackId);
      return !track?.isMuted &&
        (clip.type === 'video' || clip.type === 'audio') &&
        !!getClipExportSourceUrl(clip);
    });
  }

  /**
   * Calculate timeline duration
   */
  private calculateDuration(clips: Clip[]): number {
    if (clips.length === 0) return 0;
    return Math.max(...clips.map(c => c.start + c.duration));
  }

  /**
   * Cleanup resources
   */
  private cleanup() {
    if (this.frameRenderer) {
      this.frameRenderer.cleanup();
      this.frameRenderer = undefined;
    }
  }

  /**
   * Update progress callback
   */
  private updateProgress(progress: ExportProgress) {
    this.progressCallback(progress);
  }
}

/**
 * Default export configuration
 */
export const DEFAULT_EXPORT_CONFIG: ExportConfig = {
  aspectRatio: '16:9',
  fps: 30,
  videoBitrate: 5_000_000, // 5 Mbps
  audioBitrate: 128_000,   // 128 kbps
  audioSampleRate: 48000,
  format: 'webm',
};
